import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError, NetworkError, api, type AppointmentView, type PatientSummary, type WhatsAppDraft } from '../api';
import { useI18n } from '../i18n';
import { PatientPicker } from '../components/PatientPicker';
import { describe } from '../errors';
import {
  AppointmentStatus,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Skeleton,
  Spinner,
  WhatsAppDialog,
} from '../components';
import { DayGrid, MonthGrid, WeekGrid, dayBounds, type CalendarView } from '../components/Calendar';
import { IconPlus, IconSearch } from '../icons';

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Monday-free week: starts on Sunday, which is the working week here. */
function weekOf(day: string): string[] {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() - d.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(d);
    x.setDate(d.getDate() + i);
    return isoDay(x);
  });
}

type Resources = Awaited<ReturnType<typeof api.appointmentResources>>;

/**
 * The schedule.
 *
 * It used to be a table of one day, which tells you what is booked but never
 * what is free — and "what is free" is the question a reception desk is asked
 * all day. So it is a calendar, drawn to scale, and the day view splits into
 * COLUMNS: one per dentist, or one per chair, whichever the clinic is
 * currently short of.
 *
 * The filters are the other half of the same problem. One dentist's day, one
 * chair's day, or one patient's appointments past and future — each is a
 * question people actually ask, and each was previously answered by scrolling.
 *
 * Nothing here deletes an appointment. Cancelling keeps the row, with a reason,
 * because "the patient cancelled" and "we never booked them" are different
 * facts; the slot becomes free again because the database's overlap constraint
 * ignores cancelled rows, not because anything was erased.
 */
export function Appointments({ clinicId, canWrite }: { clinicId: string; canWrite: boolean }) {
  const { t, locale, formatDate } = useI18n();
  const [view, setView] = useState<CalendarView>('day');
  const [splitBy, setSplitBy] = useState<'dentist' | 'chair'>('dentist');
  const [day, setDay] = useState(() => isoDay(new Date()));
  const [rows, setRows] = useState<AppointmentView[] | null>(null);
  const [resources, setResources] = useState<Resources | null>(null);
  const [dentistId, setDentistId] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [patientQuery, setPatientQuery] = useState('');
  const [patientId, setPatientId] = useState('');
  const [patientOptions, setPatientOptions] = useState<PatientSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [open, setOpen] = useState<AppointmentView | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    api.appointmentResources().then(setResources).catch(() => setResources(null));
  }, [clinicId]);

  // Looking someone up by name, rather than asking for an id nobody has.
  useEffect(() => {
    if (patientQuery.trim().length < 2) {
      setPatientOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      api
        .patients({ q: patientQuery.trim(), limit: 8 })
        .then((r) => setPatientOptions(r.patients))
        .catch(() => setPatientOptions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [patientQuery]);

  const range = useMemo(() => {
    if (view === 'day') {
      const from = new Date(`${day}T00:00:00`);
      return { from, to: new Date(from.getTime() + 86_400_000) };
    }
    if (view === 'week') {
      const days = weekOf(day);
      const from = new Date(`${days[0]}T00:00:00`);
      return { from, to: new Date(new Date(`${days[6]}T00:00:00`).getTime() + 86_400_000) };
    }
    const first = new Date(`${day.slice(0, 7)}-01T00:00:00`);
    const gridStart = new Date(first);
    gridStart.setDate(1 - first.getDay());
    return { from: gridStart, to: new Date(gridStart.getTime() + 42 * 86_400_000) };
  }, [view, day]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.appointments(range.from.toISOString(), range.to.toISOString(), {
        dentistId: dentistId || undefined,
        resourceId: resourceId || undefined,
        patientId: patientId || undefined,
      });
      setRows(res.appointments);
    } catch (e) {
      setError(describe(e, t));
    }
  }, [range, dentistId, resourceId, patientId, t]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [clinicId, load, nonce]);

  const shift = (delta: number) => {
    const d = new Date(`${day}T12:00:00`);
    if (view === 'day') d.setDate(d.getDate() + delta);
    else if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setDay(isoDay(d));
  };

  const bounds = useMemo(() => dayBounds(rows ?? []), [rows]);
  const isToday = day === isoDay(new Date());

  // One column per dentist, or per chair. A filtered-to-one-dentist day still
  // shows their column, so the view never collapses to nothing.
  const columns = useMemo(() => {
    if (!resources) return [];
    if (splitBy === 'chair') {
      const chairs = resources.chairs.map((c) => ({ id: c.id, label: locale === 'ar' ? c.ar : c.en }));
      const unassigned = (rows ?? []).some((a) => !a.resourceId);
      return unassigned ? [...chairs, { id: '', label: t.appointments.noChair }] : chairs;
    }
    const dentists = resources.dentists.filter((d) => !dentistId || d.membershipId === dentistId);
    return dentists.map((d) => ({ id: d.membershipId, label: d.name }));
  }, [resources, splitBy, rows, dentistId, locale, t]);

  const heading =
    view === 'month'
      ? new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en-GB', { month: 'long', year: 'numeric' }).format(
          new Date(`${day}T12:00:00`),
        )
      : view === 'week'
        ? `${formatDate(`${weekOf(day)[0]}T12:00:00`)} – ${formatDate(`${weekOf(day)[6]}T12:00:00`)}`
        : formatDate(`${day}T12:00:00`);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.appointments.title}</h1>
          <div className="page-sub">{heading}</div>
        </div>
        {canWrite ? (
          <button type="button" className="btn" onClick={() => setBooking(true)}>
            <IconPlus size={16} />
            {t.appointments.add}
          </button>
        ) : null}
      </div>

      <div className="toolbar">
        <div className="segmented" role="group" aria-label={t.appointments.title}>
          {(['day', 'week', 'month'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>
              {(t.appointments.views as Record<string, string>)[v]}
            </button>
          ))}
        </div>

        <div className="row" style={{ gap: 4 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => shift(-1)} aria-label={t.appointments.previous}>
            {locale === 'ar' ? '›' : '‹'}
          </button>
          <button
            type="button"
            className={isToday ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
            onClick={() => setDay(isoDay(new Date()))}
          >
            {t.appointments.today}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => shift(1)} aria-label={t.appointments.next}>
            {locale === 'ar' ? '‹' : '›'}
          </button>
        </div>

        <input
          type="date"
          value={day}
          onChange={(e) => e.target.value && setDay(e.target.value)}
          className="select-inline ltr-value"
          aria-label={t.appointments.date}
        />

        <div className="toolbar-spacer" />

        {/* Filters. Each answers a question people ask out loud. */}
        <select
          className="select-inline"
          value={dentistId}
          onChange={(e) => setDentistId(e.target.value)}
          aria-label={t.appointments.dentist}
        >
          <option value="">{t.reports.allDentists}</option>
          {(resources?.dentists ?? []).map((d) => (
            <option key={d.membershipId} value={d.membershipId}>
              {d.name}
            </option>
          ))}
        </select>

        {(resources?.chairs.length ?? 0) > 0 ? (
          <select
            className="select-inline"
            value={resourceId}
            onChange={(e) => setResourceId(e.target.value)}
            aria-label={t.appointments.chair}
          >
            <option value="">{t.appointments.allChairs}</option>
            {(resources?.chairs ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {locale === 'ar' ? c.ar : c.en}
              </option>
            ))}
          </select>
        ) : null}

        <div className="search-box" style={{ maxWidth: 240, minWidth: 170 }}>
          <IconSearch size={15} className="icon" />
          <input
            value={patientQuery}
            onChange={(e) => {
              setPatientQuery(e.target.value);
              if (!e.target.value) setPatientId('');
            }}
            placeholder={t.appointments.filterPatient}
            aria-label={t.appointments.filterPatient}
          />
          {patientOptions.length > 0 && !patientId ? (
            <div className="type-ahead">
              {patientOptions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setPatientId(p.id);
                    setPatientQuery(`${p.givenName} ${p.familyName}`);
                    setPatientOptions([]);
                  }}
                >
                  <strong>
                    {p.givenName} {p.familyName}
                  </strong>
                  <span className="ltr-value">{p.patientNumber}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {view === 'day' ? (
          <div className="segmented" role="group" aria-label={t.appointments.splitBy}>
            {(['dentist', 'chair'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={splitBy === s} onClick={() => setSplitBy(s)}>
                {s === 'dentist' ? t.appointments.byDentist : t.appointments.byChair}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* A filter that is on but invisible is how people end up convinced the
          calendar has lost their appointments. */}
      {patientId || dentistId || resourceId ? (
        <div className="notice" style={{ marginBottom: 14 }}>
          <span>{t.appointments.filtered}</span>
          <button
            type="button"
            className="btn-link"
            style={{ marginInlineStart: 'auto' }}
            onClick={() => {
              setDentistId('');
              setResourceId('');
              setPatientId('');
              setPatientQuery('');
            }}
          >
            {t.appointments.clearFilters}
          </button>
        </div>
      ) : null}

      <div className="card">
        {error ? (
          <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : rows === null || !resources ? (
          <Skeleton rows={8} />
        ) : rows.length === 0 && view !== 'month' ? (
          <EmptyState
            title={t.appointments.none}
            action={
              canWrite ? (
                <button type="button" className="btn" onClick={() => setBooking(true)}>
                  {t.appointments.add}
                </button>
              ) : null
            }
          />
        ) : view === 'month' ? (
          <MonthGrid
            appointments={rows}
            month={day.slice(0, 7)}
            onPickDay={(d) => {
              setDay(d);
              setView('day');
            }}
            onOpen={setOpen}
          />
        ) : view === 'week' ? (
          <WeekGrid
            appointments={rows}
            days={weekOf(day)}
            dayStartHour={bounds.start}
            dayEndHour={bounds.end}
            onOpen={setOpen}
          />
        ) : (
          <DayGrid
            appointments={rows}
            columns={columns}
            columnOf={(a) => (splitBy === 'chair' ? (a.resourceId ?? '') : a.dentistMembershipId)}
            dayStartHour={bounds.start}
            dayEndHour={bounds.end}
            onOpen={setOpen}
          />
        )}
      </div>

      {booking ? (
        <BookModal
          day={day}
          onClose={() => setBooking(false)}
          onBooked={() => {
            setBooking(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {open ? (
        <AppointmentDetail
          appointment={open}
          canWrite={canWrite}
          resources={resources}
          onClose={() => setOpen(null)}
          onChanged={() => {
            setOpen(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * One appointment, and what can be done to it.
 *
 * Cancel, reschedule and move through the states were all missing: a booking
 * could be made and then never touched again, which is not how a diary works.
 */
function AppointmentDetail({
  appointment,
  canWrite,
  resources,
  onClose,
  onChanged,
}: {
  appointment: AppointmentView;
  canWrite: boolean;
  resources: Resources | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, tx, locale, formatDate, formatTime, personName } = useI18n();
  const [mode, setMode] = useState<'view' | 'move' | 'cancel'>('view');
  const [date, setDate] = useState(() => isoDay(new Date(appointment.startsAt)));
  const [time, setTime] = useState(() => new Date(appointment.startsAt).toTimeString().slice(0, 5));
  const [minutes, setMinutes] = useState(() =>
    Math.round((new Date(appointment.endsAt).getTime() - new Date(appointment.startsAt).getTime()) / 60_000),
  );
  const [dentist, setDentist] = useState(appointment.dentistMembershipId);
  const [chair, setChair] = useState(appointment.resourceId ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<WhatsAppDraft | null>(null);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  const nextStates: Record<string, string[]> = {
    scheduled: ['confirmed', 'checked_in', 'no_show', 'cancelled'],
    confirmed: ['checked_in', 'no_show', 'cancelled'],
    checked_in: ['in_treatment', 'cancelled'],
    in_treatment: ['completed'],
    completed: [],
    cancelled: [],
    no_show: [],
  };

  return (
    <Modal
      title={appointment.patient ? personName(appointment.patient) : t.appointments.title}
      onClose={onClose}
      footer={
        canWrite && mode === 'view' ? (
          <>
            {appointment.status !== 'cancelled' && appointment.status !== 'completed' ? (
              <>
                <button type="button" className="btn btn-danger" onClick={() => setMode('cancel')}>
                  {t.appointments.cancel}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setMode('move')}>
                  {t.appointments.reschedule}
                </button>
              </>
            ) : null}
          </>
        ) : mode === 'move' ? (
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setMode('view')} disabled={busy}>
              {t.patients.cancel}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  api.rescheduleAppointment(appointment.id, {
                    startsAt: new Date(`${date}T${time}:00`).toISOString(),
                    durationMinutes: minutes,
                    dentistMembershipId: dentist,
                    resourceId: chair || null,
                  }),
                )
              }
            >
              {busy ? <Spinner /> : t.appointments.reschedule}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setMode('view')} disabled={busy}>
              {t.patients.cancel}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy || reason.trim().length < 3}
              onClick={() =>
                void run(() => api.setAppointmentStatus(appointment.id, { status: 'cancelled', reason }))
              }
            >
              {busy ? <Spinner /> : t.appointments.cancel}
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        {draft ? <WhatsAppDialog draft={draft} onClose={() => setDraft(null)} /> : null}
        {mode === 'view' ? (
          <>
            <dl className="detail-list">
              <div>
                <dt>{t.appointments.time}</dt>
                <dd className="ltr-value">
                  {formatDate(appointment.startsAt)} · {formatTime(appointment.startsAt)}–
                  {formatTime(appointment.endsAt)}
                </dd>
              </div>
              <div>
                <dt>{t.appointments.dentist}</dt>
                <dd>{appointment.dentistName}</dd>
              </div>
              <div>
                <dt>{t.appointments.chair}</dt>
                <dd>
                  {appointment.resource ? (locale === 'ar' ? appointment.resource.ar : appointment.resource.en) : '—'}
                </dd>
              </div>
              <div>
                <dt>{t.appointments.reason}</dt>
                <dd>{appointment.reason ?? '—'}</dd>
              </div>
            </dl>

            <div className="row" style={{ flexWrap: 'wrap' }}>
              <AppointmentStatus status={appointment.status} />
              {appointment.provisional && appointment.overlapNotedAt ? (
                <span className="badge danger">{t.appointments.overlap}</span>
              ) : appointment.provisional ? (
                <span className="badge warn">{t.appointments.provisional}</span>
              ) : null}
            </div>

            {/* The reminder the reception desk sends. The message was
                composable from the API from the start; nothing on this screen
                asked for it. */}
            {appointment.patient && appointment.status !== 'cancelled' && appointment.status !== 'completed' ? (
              <div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      const r = await api.appointmentWhatsApp(appointment.id, { locale });
                      setDraft(r.draft);
                    } catch (e) {
                      setError(
                        describe(e, t),
                      );
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {t.appointments.whatsapp}
                </button>
                <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12, marginTop: 4 }}>
                  {t.appointments.whatsappHint}
                </span>
              </div>
            ) : null}

            {appointment.cancelReason ? (
              <div className="notice warn">
                <span>{appointment.cancelReason}</span>
              </div>
            ) : null}

            {canWrite && nextStates[appointment.status]?.length ? (
              <div>
                <div className="section-label">{t.appointments.moveTo}</div>
                <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {nextStates[appointment.status]!
                    .filter((s) => s !== 'cancelled')
                    .map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busy}
                        onClick={() => void run(() => api.setAppointmentStatus(appointment.id, { status: s }))}
                      >
                        {(t.appointments.states as Record<string, string>)[s] ?? s}
                      </button>
                    ))}
                </div>
              </div>
            ) : null}
          </>
        ) : mode === 'move' ? (
          <>
            <div className="notice">
              <span>{t.appointments.rescheduleNote}</span>
            </div>
            <div className="grid-2">
              <Field label={t.appointments.date} required>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ltr-value" />
              </Field>
              <Field label={t.appointments.time} required>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="ltr-value" />
              </Field>
              <Field label={t.appointments.duration} required>
                <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
                  {[15, 20, 30, 45, 60, 75, 90, 120].map((m) => (
                    <option key={m} value={m}>
                      {tx(t.appointments.minutes, { n: m })}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t.appointments.dentist} required>
                <select value={dentist} onChange={(e) => setDentist(e.target.value)}>
                  {(resources?.dentists ?? []).map((d) => (
                    <option key={d.membershipId} value={d.membershipId}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t.appointments.chair}>
                <select value={chair} onChange={(e) => setChair(e.target.value)}>
                  <option value="">—</option>
                  {(resources?.chairs ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {locale === 'ar' ? c.ar : c.en}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </>
        ) : (
          <>
            <div className="notice warn">
              <span>{t.appointments.cancelNote}</span>
            </div>
            <Field label={t.appointments.cancelReason} required>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

function BookModal({ day, onClose, onBooked }: { day: string; onClose: () => void; onBooked: () => void }) {
  const { t, tx, locale } = useI18n();
  const [resources, setResources] = useState<Resources | null>(null);
  const [, setPatients] = useState<PatientSummary[]>([]);
  const [form, setForm] = useState({
    patientId: '',
    dentistMembershipId: '',
    resourceId: '',
    date: day,
    time: '09:00',
    durationMinutes: 30,
    reason: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([api.appointmentResources(), api.patients({ limit: 100 })])
      .then(([r, p]) => {
        setResources(r);
        setPatients(p.patients);
        setForm((f) => ({
          ...f,
          dentistMembershipId: r.dentists[0]?.membershipId ?? '',
          resourceId: r.chairs[0]?.id ?? '',
        }));
      })
      .catch(() => setError(t.common.error));
  }, [t]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createAppointment({
        patientId: form.patientId || null,
        dentistMembershipId: form.dentistMembershipId,
        resourceId: form.resourceId || null,
        startsAt: new Date(`${form.date}T${form.time}:00`).toISOString(),
        durationMinutes: form.durationMinutes,
        reason: form.reason || undefined,
      });
      onBooked();
    } catch (err) {
      // The overlap refusal comes from a database constraint, not a check the
      // interface performed, so the message is the server's (spec §9, T11).
      setError(
        err instanceof ApiError ? err.message : err instanceof NetworkError ? t.errors.network : t.common.error,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.appointments.newTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="submit" form="book" className="btn" disabled={busy || !form.dentistMembershipId}>
            {busy ? (
              <>
                <Spinner /> {t.patients.saving}
              </>
            ) : (
              t.appointments.book
            )}
          </button>
        </>
      }
    >
      <form id="book" onSubmit={submit} className="stack" noValidate>
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <Field label={t.appointments.patient} hint={t.appointments.patientHint}>
          <PatientPicker value={form.patientId} onChange={(id) => setForm({ ...form, patientId: id })} allowNone />
        </Field>

        <div className="grid-2">
          <Field label={t.appointments.dentist} required>
            <select
              value={form.dentistMembershipId}
              onChange={(e) => setForm({ ...form, dentistMembershipId: e.target.value })}
              required
            >
              {(resources?.dentists ?? []).map((d) => (
                <option key={d.membershipId} value={d.membershipId}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.appointments.chair}>
            <select value={form.resourceId} onChange={(e) => setForm({ ...form, resourceId: e.target.value })}>
              <option value="">—</option>
              {(resources?.chairs ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {locale === 'ar' ? c.ar : c.en}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.appointments.date} required>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="ltr-value"
              required
            />
          </Field>
          <Field label={t.appointments.time} required>
            <input
              type="time"
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              className="ltr-value"
              required
            />
          </Field>
        </div>

        <Field label={t.appointments.duration} hint={t.appointments.durationHint}>
          <select
            value={form.durationMinutes}
            onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })}
          >
            {[15, 20, 30, 45, 60, 75, 90, 120].map((m) => (
              <option key={m} value={m}>
                {tx(t.appointments.minutes, { n: m })}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t.appointments.reason} hint={t.patients.optional}>
          <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </form>
    </Modal>
  );
}
